import { ThumbnailProps } from '@wds/thumbnail/dist/esm/Thumbnail.types';
import { Maybe } from 'graphql/jsutils/Maybe';

import {
  StoriesPropsGeneratorArgs,
  ThumbnailPropsGeneratorArgs
} from 'src/utils/constants/TvShowDetailsPageConstants';

import { BreakPointsConfig } from '../../components/rails/railsHelper';
import {
  TvShowRelatedContent,
  Episode
} from '../../services/GraphQL/Queries/types';
import { Genre } from '../../types/consumption';
import { Posts } from '../../types/tvshowdetails';
import { ASSET_TYPE } from '../constants/AppConstants';
import { Languages } from '../constants/stringConstants';
import { TAG_NAME } from '../constants/TagNameConstants';
import { getEpisodeReleaseDate } from '../ConsumptionHelper/ConsumptionHelper';
import { getDeviceWidth } from '../getDeviceWidth';
import { getImageURL } from '../ImageHelper/ImageURLGenerator';

export const thumbnailPropsGenerator = ({
  data,
  showAddToWatchlistButton,
  isAvailableOnWatchlist,
  onCardClick,
  onWatchlistClick,
  onShareClick,
  translationText,
  breakPointsConfig,
  index,
  deviceType
}: ThumbnailPropsGeneratorArgs): ThumbnailProps => {
  const thumbnailProps: ThumbnailProps = {
    ageRating: data.ageRating || '',
    assetType: data.assetType || ASSET_TYPE.Episode,
    audioLanguages: getAudioLanuages(data.audioLanguages) || [],
    description:
      data.assetType !== ASSET_TYPE.Episode ? data.description ?? '' : '',
    duration: data.duration || undefined,
    episodeNumber: data.episodeNumber || undefined,
    genres: (data.genres || []) as Genre[],
    icon: undefined,
    id: data.id || '',
    image: getImage(data),
    onCardClick,
    onWatchButtonClick: onCardClick,
    onWatchlistClick,
    onShareClick,
    originalTitle: data.originalTitle || '',
    releaseDate:
      (data.releaseDate && getEpisodeReleaseDate(data.releaseDate)) || '',
    showDetails: true,
    slug: data.slug || '',
    shareUrl: data.slug || '',
    title: data.title || '',
    showAddToWatchlistButton,
    isAvailableOnWatchlist,
    horizontalPosition: getHorizontalPosition(index, breakPointsConfig),
    type: 'horizontal',
    translation_text: translationText,
    showWatchNowButton: true,
    deviceType,
    index: index
  };

  return thumbnailProps;
};

const getTagName = (assetType: number) => {
  switch (assetType) {
    case ASSET_TYPE.Movie:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Episode:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Season:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Album:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.ContentSet:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Track:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.TvShow:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Collection:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Channel:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.EpgProgram:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.SubscriptionPlan:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Notification:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.PromoCode:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.LiveEvent:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.File:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.GenreList:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Unknown:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    case ASSET_TYPE.Custom:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
    default:
      return TAG_NAME.DIFFERENT_EPISODE_LIST;
  }
};
const getType = (data: TvShowRelatedContent | Episode) => {
  return 'horizontal';
};
const getImage = (data: TvShowRelatedContent | Episode) => {
  let image = {};
  if (data.image && data.assetType) {
    const imageUrl = getImageURL({
      deviceWidth: getDeviceWidth() || 0,
      tagName: getTagName(data.assetType),
      item: {
        id: data.id || '',
        title: data.title || '',
        assetType: data.assetType,
        image: data.image
      }
    });

    image = { horizontal: imageUrl };
    // } else {
    //   image = { horizontal: data.image_original - uncomment this and story image will come as direct image url comes
    //   };
  }
  return image;
};

const getAudioLanuages = (
  audioLanguagesKeys: Maybe<Maybe<string>[]> | undefined
): string[] => {
  let transformedAudioLanguages: string[] = [];
  const languageKeys = Object.keys(Languages);
  if (audioLanguagesKeys && audioLanguagesKeys.length) {
    transformedAudioLanguages = audioLanguagesKeys.map(
      (audioLanguageKey: Maybe<string>) => {
        if (audioLanguageKey && languageKeys.includes(audioLanguageKey)) {
          const audioLanguage = Languages[
            audioLanguageKey as keyof typeof Languages
          ] as string;
          return audioLanguage ? audioLanguage : '';
        }
        return '';
      }
    );
  }
  return transformedAudioLanguages;
};

const getHorizontalPosition = (
  index: number,
  breakPointsConfig: BreakPointsConfig | null | undefined
): ThumbnailProps['horizontalPosition'] => {
  if (!breakPointsConfig) {
    return 'middle';
  }
  const deviceWidth = getDeviceWidth();
  if (!deviceWidth) {
    return 'middle';
  }
  if (deviceWidth > 1800) {
    return calculateHorizontalPosition(index, breakPointsConfig.xl);
  }
  if (deviceWidth > 1200) {
    return calculateHorizontalPosition(index, breakPointsConfig.default);
  }
  if (deviceWidth <= 1200) {
    return calculateHorizontalPosition(index, breakPointsConfig.lg);
  }
  if (deviceWidth <= 900) {
    return calculateHorizontalPosition(index, breakPointsConfig.md);
  }
  if (deviceWidth <= 600) {
    return calculateHorizontalPosition(index, breakPointsConfig.sm);
  }
  return 'middle';
};

const calculateHorizontalPosition = (
  index: number,
  numberOfItemsInEachRow: number
): ThumbnailProps['horizontalPosition'] => {
  if (index % numberOfItemsInEachRow === 0) {
    return 'left';
  }
  if (index % numberOfItemsInEachRow === numberOfItemsInEachRow - 1) {
    return 'right';
  }
  return 'middle';
};

export const isTypePost = (obj: Episode | Posts): obj is Posts => {
  return 'excerpt' in obj;
};

export const storiesPropsGenerator = ({
  post,
  index,
  breakPointsConfig,
  translationText,
  deviceType,
  onShareClick
}: StoriesPropsGeneratorArgs): ThumbnailProps => {
  // Add mixpanel for story rails
  const navigate = () => window.location.assign(post.link);
  return {
    ageRating: '',
    assetType: undefined,
    audioLanguages: [],
    description: '',
    duration: undefined,
    episodeNumber: undefined,
    genres: [],
    icon: undefined,
    id: post.id || '',
    image: { horizontal: post.image_original },
    onCardClick: navigate,
    onWatchButtonClick: navigate,
    onWatchlistClick: navigate,
    onShareClick,
    originalTitle: '',
    releaseDate:
      (post.published_date && getEpisodeReleaseDate(post.published_date)) || '',
    showDetails: true,
    slug: post.link || '',
    shareUrl: post.link || '',
    title: post.title || '',
    showAddToWatchlistButton: false,
    isAvailableOnWatchlist: false,
    horizontalPosition: getHorizontalPosition(index, breakPointsConfig),
    type: 'horizontal',
    translation_text: translationText,
    showWatchNowButton: true,
    deviceType,
    index: index
  };
};
